import { useEffect, useRef } from 'react';
import { useAppDispatch } from '../../redux/app/hooks';
import { useChat } from './useChat';
import { HostContext } from '../../embed-script/HostContext';
import { setServiceInfo } from '../../redux/features/app/appSlice';
import { getParentTargetOrigin, onParentOriginLearned } from '../parentOrigin';
import { useAuth } from 'react-oidc-context';

export const useInitialServiceInfo = (hostContext: HostContext) => {
    const dispatch = useAppDispatch();
    const chat = useChat();
    const auth = useAuth();
    // Post the flags at most once per hook lifetime, even if the origin is learned after a deferral
    // and the effect has also re-run in the meantime.
    const flagsPostedRef = useRef(false);

    // CWE-668 (#2113): featureflagsreceived carries the resolved flag set, so it must reach only the
    // validated embedder — never the '*' fallback. When no trusted target is known yet we DEFER the
    // post instead of dropping it (#2276): the embed-script seed normally gives a target from first
    // render, but on a seedless path the origin is learned only from an inbound host message — and
    // that message may be one that never re-runs this effect (its deps are [isAuthenticated,
    // hostContext], but origins are learned from any source-checked message). onParentOriginLearned
    // fires the retry once an origin arrives, so the launcher isn't stranded for the session. In
    // standalone / no-embed-script mode window.parent === window and nothing listens, so the deferred
    // post simply never fires and loses nothing.
    const postFeatureFlagsToHost = (featureFlags: Record<string, unknown>) => {
        if (flagsPostedRef.current) {
            return;
        }
        const targetOrigin = getParentTargetOrigin();
        if (targetOrigin === '*') {
            onParentOriginLearned(() => {
                postFeatureFlagsToHost(featureFlags);
            });
            return;
        }
        flagsPostedRef.current = true;
        console.log('Sending featureflagsreceived message with feature flags:', featureFlags);
        window.parent.postMessage(
            {
                event: 'featureflagsreceived',
                data: { featureFlags },
            },
            targetOrigin,
        );
    };

    const fetchAndSetServiceInfo = async () => {
        console.log('Fetching service info from SettingUserInfo');
        const serviceInfo = await chat.getServiceInfo(hostContext);
        if (serviceInfo) {
            console.log('Received service info:', serviceInfo);
            dispatch(setServiceInfo(serviceInfo));
            const serializableFeatureFlags = Object.fromEntries(
                Object.entries(serviceInfo.featureFlags).map(([key, value]) => [key, value]),
            );
            postFeatureFlagsToHost(serializableFeatureFlags);
        }
    };

    useEffect(() => {
        if (auth.isAuthenticated) {
            void fetchAndSetServiceInfo();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [auth.isAuthenticated, hostContext]);
};
